import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { createWidget, actionGroup } from 'actions/editor';

import { Button, Nav, Tab } from 'react-bootstrap';
import _ from 'lodash';
import cx from 'classnames';

import StoryboardContext from 'components/Storyboard/context';
import { MediaLibraryModal } from 'components/MediaLibrary';
import { MediaPaletteView } from "components/MediaLibrary/MediaPalette";
import { MediaUploadButton } from 'components/MediaLibrary';
import { BrandMediaPaletteView } from 'components/MediaLibrary/BrandMediaPalette';
import { ExternalMediaPalette } from 'components/MediaLibrary/ExternalMediaPalette';
import { getPositionForNewWidget } from 'components/Editor/helper/layoutManager';
import { PaletteContext } from '.';

import {
  IMAGE, VIDEO
} from 'constants/widgetTypes';

import styles from './palette.module.scss';
import { getStoryboardMixpanelEventPayload } from 'services/mixpanelUtil';
import { MIXPANEL_BRANDKIT_CREATED, MIXPANEL_WIDGET_ADDED, MIXPANEL_WIDGET_USED } from 'constants/mixpanelEvents';
import { createMixpanelEvent } from 'actions/mixpanel';

export const MediaPalette = ({ editorSession, defaultParent, target }) => {
  const dispatch = useDispatch();
  const [library, setLibrary] = useState(false);
  const [mediaType, setMediaType] = useState("image");
  const [mediaLibraryType, setMediaLibraryType] = useState("pexels");
  const [orientation, setOrientation] = useState("");
  const [activeState, setActiveState] = useState("manageMedia");

  const storyboardContext = React.useContext(StoryboardContext);
  const paletteContext = React.useContext(PaletteContext);

  const { palettes = [] } = paletteContext;
  const paletteData = palettes.find(item => item.type === 'MEDIA');
  const brandkitId = _.get(paletteData, 'brandkitId', null);
  const defaultId = _.get(paletteData, 'defaultId', null);

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);

  const parentWidget = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || []
    return widgets.find(widget => {
      return widget.uid === defaultParent
    });
  });
  const desktopParentLayout = _.get(parentWidget, `properties.desktop.layout`);
  const mobileParentLayout = _.get(parentWidget, `properties.mobile.layout`);

  const handleUploadLibrary = () => {
      setLibrary(true);
  }

  const handleCreateImage = (medias) => {
    setLibrary(false);

    actionGroup.start();

    medias.forEach((media) => {
      const naturalWidth = _.get(media, 'width') || 600;
      const naturalHeight = _.get(media, 'height') || 600;

      var desktopWidth = Math.min(600, naturalWidth);
      var desktopHeight = desktopWidth * naturalHeight / naturalWidth;

      var mobileWidth = Math.min(300, naturalWidth);
      var mobileHeight = mobileWidth * naturalHeight / naturalWidth;

      const desktopPosition = getPositionForNewWidget(editorSession, desktopParentLayout, { width: desktopWidth, height: desktopHeight });
      const mobilePosition = getPositionForNewWidget(editorSession, mobileParentLayout, { width: 300, height: desktopHeight });

      var widget = null;

      if (media.type === 'image') {
        widget = {
          type: IMAGE,
          name: "Image",
          properties: {
            "desktop": {
              "visible": true,
              "layout": {
                "transform": {
                  "translate": `${desktopPosition.x}px, ${desktopPosition.y}px`,
                  "rotate": "0deg"
                },
                "width": `${desktopWidth}px`,
                "height": `${desktopHeight}px`
              },
              "crop": null,
              "alt": null,
              "animation": null,
              "border": null,
              "position": null,
              "shadow": null
            },
            "mobile": {
              "visible": true,
              "layout": {
                "transform": {
                  "translate": `${mobilePosition.x}px, ${mobilePosition.y}px`,
                  "rotate": "0deg"
                },
                "width": `${mobileWidth}px`,
                "height": `${mobileHeight}px`
              },
              "crop": null,
              "alt": null,
              "animation": null,
              "border": null,
              "position": null,
              "shadow": null
            }
          },
          content: media
        }
      }

      if (media.type === 'video') {
        widget = {
          type: VIDEO,
          name: "Video",
          properties: {
            "desktop": {
              "visible": true,
              "layout": {
                "transform": {
                  "translate": `${desktopPosition.x}px, ${desktopPosition.y}px`,
                  "rotate": "0deg"
                },
                "width": `${desktopWidth}px`,
                "height": `${desktopHeight}px`
              },
              "animation": null,
              "shadow": {
                "color": {
                  "type": "solid",
                  "r": 255,
                  "g": 255,
                  "b": 255,
                  "a": 1
                },
                "axes": {
                  "x": "0",
                  "y": "0",
                  "b": "0"
                },
                "show": false
              },
              "border": null,
            },
            "mobile": {
              "visible": true,
              "layout": {
                "transform": {
                  "translate": `${mobilePosition.x}px, ${mobilePosition.y}px`,
                  "rotate": "0deg"
                },
                "width": `${mobileWidth}px`,
                "height": `${mobileHeight}px`
              },
              "animation": null,
              "shadow": {
                "color": {
                  "type": "solid",
                  "r": 255,
                  "g": 255,
                  "b": 255,
                  "a": 1
                },
                "axes": {
                  "x": "0",
                  "y": "0",
                  "b": "0"
                },
                "show": false
              },
              "border": null,
            }
          },
          content: media
        }
      }

      dispatch(createWidget(editorSession, [widget], defaultParent));
        var mixpanelPayload = getStoryboardMixpanelEventPayload(storyboardContext.id, storyboardContext.name, storyboardContext.type);
        mixpanelPayload = {
          ...mixpanelPayload,
          widget_type: media.type === "image" ? "Images": "Videos",
          brandkit_widget: !!media.brandkitMedia,
        }
        dispatch(createMixpanelEvent(MIXPANEL_WIDGET_USED, mixpanelPayload))
    });

    actionGroup.end();

    paletteContext.close();
  }

  const handleCloseMediaLibrary = () => {
    setLibrary(false);
  }

  const openMediaLibrary = (e) => {
    e.stopPropagation()
    handleUploadLibrary()
  }

  const handleManageMediaClick = () => {
    setActiveState("manageMedia");
  }

  const handleExternalMedia = () => {
    setActiveState("pexels");
  }

  return (
    <Tab.Pane eventKey="MEDIA" className={cx(styles.tabPane, styles.mediaPalette)}>
      <div className={styles.mediaManagement}>
        <Tab.Container id="regular-form-management-tab" defaultActiveKey="myMedia">
          <header className={styles.mediaManagementHeader}>
            <Nav variant="pills" className={styles.mediaManagementTab}>
              <Nav.Item>
                <Nav.Link eventKey="myMedia" onClick={handleManageMediaClick}>My Media</Nav.Link>
              </Nav.Item>
            </Nav>
            <h2 className={styles.headerTitle}>STOCK MEDIA</h2>
            <Nav style={{ marginTop: '-16px' }} variant="pills" className={styles.mediaManagementTab}>
              {brandkitEnabled && 
                <Nav.Item>
                  <Nav.Link eventKey="brandMedia" onClick={handleExternalMedia}> Brand Media </Nav.Link>
                </Nav.Item>
              }
              <Nav.Item>
                <Nav.Link eventKey="pexels" onClick={handleExternalMedia}> Pexels </Nav.Link>
              </Nav.Item>
            </Nav>
            <Button onClick={openMediaLibrary} className={styles.mediaLibraryBtn}>
              <i className={cx("cleverstory-grid-view", styles.uploadIcon)} />Media Library
            </Button>
          </header>
          <Tab.Content className={styles.mediaContentContainer}>
            <MediaLibraryModal
              show={library}
              setLibrary={setLibrary}
              storyboard={storyboardContext}
              target={target}
              editorSession={editorSession}
              multiple
              onSelect={handleCreateImage}
              onClose={handleCloseMediaLibrary}
            />
            <Tab.Pane eventKey="myMedia">
              <MediaPaletteView
                mediaLibrary={library}
                multiple
                handleMediaLibrary={handleUploadLibrary}
                onSelect={handleCreateImage}
                storyboard={storyboardContext}
                target={target}
              />
            </Tab.Pane>
            <Tab.Pane eventKey="brandMedia">
              <BrandMediaPaletteView
                palette={brandkitId}
                multiple
                handleMediaLibrary={handleUploadLibrary}
                onSelect={handleCreateImage}
                storyboard={storyboardContext}
              />
            </Tab.Pane>
            <Tab.Pane eventKey="pexels">
              <ExternalMediaPalette
                mediaLibrary={library}
                onSelect={handleCreateImage}
                onClose={handleCloseMediaLibrary}
                multiple
                handleMediaLibrary={handleUploadLibrary}
                mediaType={mediaType}
                active={activeState}
                orientation={orientation}
                mediaLibraryType={mediaLibraryType}
                editorSession={editorSession}
                target={target}
              />
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </div>
    </Tab.Pane>
  );
}